import React, { useEffect, useState } from 'react';
import { CheckCircle, Package, Truck, ArrowLeft } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { Button } from './ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CheckoutSuccess = () => {
  const { clearCart } = useCart();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paymentStatus, setPaymentStatus] = useState('checking');

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');

    if (sessionId) {
      pollPaymentStatus(sessionId);
    } else {
      setLoading(false);
    }
  }, []);

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setPaymentStatus('timeout');
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/checkout/status/${sessionId}`);
      if (!response.ok) throw new Error('Failed to check status');

      const data = await response.json();

      if (data.payment_status === 'paid') {
        setPaymentStatus('paid');
        clearCart();
        
        // Fetch order details
        const orderResponse = await fetch(`${API_URL}/api/orders/by-payment/${sessionId}`);
        if (orderResponse.ok) {
          const orderData = await orderResponse.json();
          setOrder(orderData);
        }
        setLoading(false);
      } else if (data.status === 'expired') {
        setPaymentStatus('expired');
        setLoading(false);
      } else {
        setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
      }
    } catch (error) {
      console.error('Error checking payment:', error);
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Verificando tu pago...</p>
        </div>
      </div>
    );
  }

  if (paymentStatus !== 'paid') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Package className="w-8 h-8 text-yellow-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Estado del Pago</h1>
          <p className="text-gray-600 mb-6">
            {paymentStatus === 'timeout' && 'No pudimos verificar el pago. Por favor revisa tu correo electrónico para confirmación.'}
            {paymentStatus === 'expired' && 'La sesión de pago ha expirado. Por favor intenta de nuevo.'}
          </p>
          <Button 
            onClick={() => window.location.href = '/'}
            className="bg-cyan-600 hover:bg-cyan-700"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Volver al Inicio
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-green-500 to-emerald-600 p-8 text-white text-center">
            <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-12 h-12" />
            </div>
            <h1 className="text-3xl font-bold mb-2">¡Pago Exitoso!</h1>
            <p className="opacity-90">Gracias por tu compra en One2Tees USA</p>
          </div>

          {/* Order Details */}
          <div className="p-8">
            {order && (
              <>
                <div className="mb-6">
                  <h2 className="text-lg font-semibold text-gray-900 mb-2">Detalles del Pedido</h2>
                  <p className="text-sm text-gray-500">Orden #{order.id.slice(0, 8).toUpperCase()}</p>
                </div>

                {/* Items */}
                <div className="space-y-3 mb-6">
                  {order.items.map((item, idx) => (
                    <div key={idx} className="flex justify-between items-center py-2 border-b">
                      <div>
                        <p className="font-medium">{item.product_name}</p>
                        <p className="text-sm text-gray-500">Tamaño: {item.size}" × {item.quantity}</p>
                      </div>
                      <p className="font-semibold">${(item.unit_price * item.quantity).toFixed(2)}</p>
                    </div>
                  ))}
                </div>

                {/* Totals */}
                <div className="bg-gray-50 rounded-lg p-4 space-y-2 mb-6">
                  <div className="flex justify-between text-sm">
                    <span>Subtotal:</span>
                    <span>${order.subtotal.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span>Envío ({order.shipping_service}):</span>
                    <span>${order.shipping_cost.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span>Impuesto (7% FL):</span>
                    <span>${order.sales_tax.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between font-bold text-lg border-t pt-2">
                    <span>Total:</span>
                    <span className="text-green-600">${order.total.toFixed(2)}</span>
                  </div>
                </div>

                {/* Shipping Address */}
                <div className="mb-6">
                  <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                    <Truck className="w-4 h-4" />
                    Dirección de Envío
                  </h3>
                  <div className="text-sm text-gray-600">
                    <p>{order.shipping_address.name}</p>
                    <p>{order.shipping_address.street}</p>
                    <p>{order.shipping_address.city}, {order.shipping_address.state} {order.shipping_address.zip_code}</p>
                  </div>
                </div>
              </>
            )}

            <div className="text-center">
              <p className="text-sm text-gray-500 mb-4">
                Recibirás un correo electrónico con los detalles de tu pedido y número de seguimiento.
              </p>
              <Button 
                onClick={() => window.location.href = '/'}
                className="bg-cyan-600 hover:bg-cyan-700"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Volver al Inicio
              </Button>
            </div>
          </div>
        </div>

        {/* Contact Info */}
        <div className="text-center mt-6 text-sm text-gray-500">
          <p>¿Tienes preguntas? Contáctanos:</p>
          <p className="font-medium">561-310-5774 | 561-568-7620</p>
        </div>
      </div>
    </div>
  );
};

export default CheckoutSuccess;
